import {
	amountFormatter,
	phoneRegExp,
} from '../../utils/utils';
import { EmployeeController, WalletController } from '../../controllers';
import { appStore, employeeStore, userStore } from '../../stores';
import { MimaToastUtil } from '../../components';
import { object, string, number } from 'yup';
import constant from '../../utils/constant';
import { AddEmployee } from '../../pages';
import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useGetAllActiveReviews } from '../../controllers/SurveyController';

const AddEmployeesContainer = ({ closeModal, setShowSurvey }) => {
	const [stage2, setStage2] = useState(false);
	const [stage3, setStage3] = useState(false);
	const [success, setSuccess] = useState(false);
	const [loading, setLoading] = useState(false);
	const [accountName, setAccountName] = useState('');

	const { needFeedback } = useGetAllActiveReviews(constant.FEEDBACK.EMPLOYEEADD);

	const showStage2 = () => {
		setStage2(true);
		setStage3(false);
	};

	const showStage3 = () => {
		setStage2(false);
		setStage3(true);
	};

	const showSuccess = () => {
		setStage3(false);
		setSuccess(true);
	};

	const showStage1 = () => {
		setStage2(false);
		setStage3(false);
		setSuccess(false);
	};

	const goBack = () => {
		if (stage2) {
			showStage1();
		} else if (stage3) {
			showStage2();
		} else {
			closeModal();
		}
	};

	const userInfoSchema = () =>
		object({
			fullname: string()
				.required('Full Name is required')
				.test('fullName', 'First and Last name is required', function (value) {
					const names = value ? value.split(' ') : [];
					return names.length > 1 && names[0].length > 1 && names[1].length > 1;
				}),
			mobile: string()
				.matches(phoneRegExp, 'Phone number is not valid')
				.required('Phone number is required'),
			email: string()
				.trim()
				.email('Enter a valid Email')
				.required('Email Address is required'),
			gender: string().required('Gender is required'),
		});
	const jobInfoSchema = () =>
		object({
			jobTitle: string().required('Job role is required'),
			employmentType: string().required('Employment Type is required'),
			settlementCycle: string().required('Settlement Cycle is required'),
			grossPay: number().test(function () {
				const { grossPay, employmentType } = this.parent;
				return grossPay === 0 && employmentType === 'FULLTIME'
					? this.createError({
						message: 'Monthly gross pay is required for a full time position',
						path: 'grossPay',
					})
					: true;
			}),
			basicSalary: number().test(function () {
				const { basicSalary, employmentType, deductions } = this.parent;
				return basicSalary === 0
				&& employmentType === 'FULLTIME'
				&& (deductions.pension || deductions.tax)
					? this.createError({
						message: 'Monthly basic salary is required to calculate Pension and NHF',
						path: 'basicSalary',
					})
					: true;
			}),
		});
	const bankInfoSchema = () =>
		object({
			bankCode: string().required('Select a bank'),
			accountNumber: string().required('Account Number is required'),
		});

	const verifyAccountNumber = async payload => {
		if (
			payload.beneficiaryBank &&
			payload.accountNumber &&
			payload.accountNumber.length === 10
		) {
			const { status, data, errorMessage } =
				await WalletController.verifyAccountNumber(payload);
			if (status === constant.Success) {
				return data.accountName;
			} else {
				MimaToastUtil.error({
					message: errorMessage,
				});
			}
		}
		return '';
	};

	const onSubmitUserInfo = data => {
		const names = data.fullname.split(' ');
		delete data.fullname;
		const payload = {
			userInfo: {
				firstname: names[0],
				lastname: names[1],
				...data,
			},
		};
		employeeStore.setTrackCreateEmployee(payload);

		localStorage.setItem(constant.RouteParams, JSON.stringify(payload));
		showStage2();
	};

	const onSubmitJobInfo = data => {
		if (data.department === '') delete data.department;
		if (data.employeeGroup === '') delete data.employeeGroup;

		delete data.departmentName;
		delete data.employeeGroupName;

		const payload = JSON.parse(localStorage.getItem(constant.RouteParams));
		payload.jobInfo = data;
		employeeStore.setTrackCreateEmployee(payload);
		localStorage.setItem(constant.RouteParams, JSON.stringify(payload));
		showStage3();
	};

	const onSubmitBankInfo = async data => {
		setLoading(true);
		data.accountName = accountName;
		const payload = JSON.parse(localStorage.getItem(constant.RouteParams));
		payload.bankDetails = data;
		employeeStore.setTrackCreateEmployee(payload);
		const { status, errorMessage } = await EmployeeController.createEmployee(
			payload
		);
		if (status === constant.Success) {
			setLoading(false);
			employeeStore.setTrackCreateEmployee({});
			localStorage.setItem(constant.RouteParams, JSON.stringify({}));
			MimaToastUtil.success({
				message: status,
			});
			 closeModal();
			 if (needFeedback._id) {
				setShowSurvey(true)
				userStore.setReviewId(needFeedback._id)
			}
			return;
		}
		setLoading(false);
		localStorage.setItem(constant.RouteParams, JSON.stringify({}));
		employeeStore.setTrackCreateEmployee({});
		MimaToastUtil.error({
			message: errorMessage,
		});
		return closeModal();
	};

	const totalExtra = extra => {
		const totalAmount =
			extra?.reduce(
				(previousValue, currentValue) => previousValue + currentValue.amount,
				0
			) || 0;
		return amountFormatter().format(totalAmount);
	};

	return (
		<AddEmployee
			stage2={stage2}
			stage3={stage3}
			trackCreate={employeeStore.trackCreate}
			accountName={accountName}
			setAccountName={setAccountName}
			userInfoSchema={userInfoSchema}
			jobInfoSchema={jobInfoSchema}
			bankInfoSchema={bankInfoSchema}
			onSubmitUserInfo={onSubmitUserInfo}
			onSubmitJobInfo={onSubmitJobInfo}
			onSubmitBankInfo={onSubmitBankInfo}
			verifyAccountNumber={verifyAccountNumber}
			showStage2={showStage2}
			setLoading={setLoading}
			loading={loading}
			banks={appStore.banks.map(bank => {
				return { id: bank.key, value: bank.value };
			})}
			showStage3={showStage3}
			totalExtra={totalExtra}
			success={success}
			showSuccess={showSuccess}
			closeModal={closeModal}
			showStage1={showStage1}
			goBack={goBack}
		/>
	);
};

export default observer(AddEmployeesContainer);
